/**
 * QZ Tray integration.
 *
 * Wraps the `qz-tray` client library so the rest of the app never talks to
 * `qz` directly. Responsible for:
 *  - detecting whether QZ Tray is installed / running
 *  - establishing a (signed, where configured) websocket connection
 *  - discovering available printers
 *  - silently printing an in-memory image/PDF with NO browser dialog
 *
 * QZ Tray requires every message to be signed in "secure" mode. In
 * production, wire up `REACT_APP_QZ_SIGN_URL` (a backend endpoint that
 * signs the request with your QZ certificate's private key) so operators
 * are never prompted. Without it, QZ Tray will show a one-time trust
 * prompt per session — acceptable for evaluation/dev, not for production.
 * See CONFIGURATION.md.
 */

import qz from "qz-tray"
import { getExhibitionHeader } from "./exhibitionContext"

export type QzConnectionState = "not-installed" | "not-running" | "connected" | "error"

const SIGN_URL = process.env.REACT_APP_QZ_SIGN_URL || "http://localhost:8000/api/badges/qz-sign/"
const CERT_URL = process.env.REACT_APP_QZ_CERT_URL || "http://localhost:8000/api/badges/qz-cert/"

// The sign endpoint requires authentication (see apps/badges/qz_signing_views.py),
// same as every other badge/print endpoint this app calls.
function signRequestHeaders(): Record<string, string> {
  const token = localStorage.getItem("accessToken")
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...getExhibitionHeader(),
  }
}

let configured = false

function configureSecurity() {
  if (configured) return
  configured = true

  // Must match the hash used server-side in qz_signing_views.py
  // (hashes.SHA512()). A mismatch here doesn't break the initial cert
  // handshake, but it does make QZ Tray silently reject the per-request
  // signature — which shows up as the "Remember this decision" checkbox
  // being greyed out in the trust dialog, even though the certificate
  // itself shows as verified.
  qz.security.setSignatureAlgorithm("SHA512")

  if (CERT_URL) {
    qz.security.setCertificatePromise((resolve, reject) => {
      fetch(CERT_URL)
        .then((r) => {
          if (!r.ok) throw new Error(`Failed to fetch QZ certificate (${r.status})`)
          return r.text()
        })
        .then(resolve)
        .catch(reject)
    })
  }

  if (SIGN_URL) {
    qz.security.setSignaturePromise((toSign: string) => {
      return (resolve: (v: string) => void, reject: (e: any) => void) => {
        fetch(SIGN_URL, {
          method: "POST",
          headers: signRequestHeaders(),
          body: JSON.stringify({ request: toSign }),
        })
          .then(async (r) => {
            const data = await r.json().catch(() => ({}))
            if (!r.ok || !data.signature) {
              // Surface WHY signing failed (e.g. 401 = missing/expired auth
              // token) instead of silently resolving undefined, which QZ
              // Tray treats as an anonymous/untrusted request and falls
              // back to the "Untrusted website" prompt.
              throw new Error(data.message || `QZ sign request failed (${r.status})`)
            }
            return data.signature as string
          })
          .then(resolve)
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error("[QZ Tray] Signing failed — falling back to the untrusted-site prompt:", err)
            reject(err)
          })
      }
    })
  } else {
    // Dev/eval fallback: unsigned requests. QZ Tray will prompt the operator
    // once per session to trust this site. Do not ship this to production —
    // configure REACT_APP_QZ_SIGN_URL instead.
    // eslint-disable-next-line no-console
    console.warn(
      "[QZ Tray] No REACT_APP_QZ_SIGN_URL configured — running unsigned. " +
        "Operators will see a one-time trust prompt. See CONFIGURATION.md."
    )
    qz.security.setSignaturePromise(() => (resolve: (v: string) => void) => resolve(""))
  }
}

/** Is the QZ Tray desktop app installed/reachable at all? */
export async function isQzInstalled(): Promise<boolean> {
  try {
    // qz-tray exposes no direct "is installed" probe; attempting a websocket
    // connect and catching the connection-refused case is the standard way
    // consumers of this library detect absence of the local QZ Tray process.
    await connectToQzTray()
    return true
  } catch {
    return false
  }
}

export function isQzRunning(): boolean {
  return qz.websocket.isActive()
}

/** Establish (or reuse) the secure websocket connection to the local QZ Tray instance. */
export async function connectToQzTray(): Promise<void> {
  configureSecurity()

  if (qz.websocket.isActive()) {
    return
  }

  try {
    await qz.websocket.connect({ retries: 2, delay: 1 })
  } catch (err) {
    throw new Error(
      "Could not reach QZ Tray. Make sure it is installed and running on this workstation."
    )
  }
}

export async function disconnectQzTray(): Promise<void> {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect()
  }
}

export interface QzPrinter {
  name: string
}

/** List printers configured on this workstation. */
export async function listPrinters(): Promise<QzPrinter[]> {
  await connectToQzTray()
  // qz-tray's typings report `string | string[]` here (a single match comes
  // back as a bare string, multiple matches as an array). Go through
  // `unknown` first so the cast holds regardless of how the installed
  // qz-tray version's .d.ts actually shapes this return value.
  const found = (await qz.printers.find()) as unknown
  const printers: string[] = Array.isArray(found) ? (found as string[]) : [found as string]
  return printers.map((name) => ({ name }))
}

export async function getDefaultPrinter(): Promise<string | null> {
  await connectToQzTray()
  try {
    const printer = await qz.printers.getDefault()
    return printer || null
  } catch {
    return null
  }
}

export interface SilentPrintOptions {
  printerName: string
  /** Base64-encoded image or PDF bytes (no data: prefix). */
  base64Data: string
  /** 'pdf' or 'png'/'image' */
  format: "pdf" | "image"
  /** Physical page size in inches, matching the existing badge design (e.g. A6). */
  size?: { width: number; height: number }
}

/**
 * Sends the given file straight to the printer via QZ Tray.
 * No browser print dialog, no preview, no window.print() anywhere.
 */
export async function silentPrint(options: SilentPrintOptions): Promise<void> {
  await connectToQzTray()

  const cfg = qz.configs.create(options.printerName, {
    scaleContent: true,
    rasterize: options.format === "pdf",
    size: options.size ? { width: options.size.width, height: options.size.height } : undefined,
    units: "in",
  })

  // qz-tray's own PrintData type expects `type`/`format`/`flavor` to be its
  // internal string-literal unions, so this plain object literal never
  // structurally matches even though it's exactly the shape QZ Tray expects
  // at runtime. Go through `unknown` first so this compiles regardless of
  // exactly how the installed qz-tray version's .d.ts models the parameter.
  const printData = [
    {
      type: "pixel",
      format: options.format === "pdf" ? "pdf" : "image",
      flavor: "base64",
      data: options.base64Data,
    },
  ]

  try {
    await qz.print(cfg, printData as unknown as Parameters<typeof qz.print>[1])
  } catch (err: any) {
    throw new Error(err?.message || "QZ Tray failed to print the badge.")
  }
}

export async function getConnectionState(): Promise<QzConnectionState> {
  try {
    if (qz.websocket.isActive()) return "connected"
    await connectToQzTray()
    return "connected"
  } catch {
    return "not-running"
  }
}